import PopupHeader from "../../../components/Popups/PopupHeader";
import { ChangeEvent, MouseEvent, useCallback, useEffect, useState } from "react";
import Buttons, { Btn } from "../../../components/Buttons/Buttons";
import PopupFooter from "../../../components/Popups/PopupFooter";

import { UploadDataFileRequest } from "../../../api/models";
import InputField from "../../../components/Form/InputField";
import { ReactComponent as AutogenerateIcon } from '../../../assets/Icons/adornments/Generate.svg';
import { ReactComponent as PlusIcon } from '../../../assets/Icons/adornments/Plus.svg';
import DragAndDropRawFile from "../../../components/Form/Upload/DragAndDropRawFile";
import SelectedItem from "../../../components/Form/Upload/SelectedItem";
import { getFileExt, formatBytes, getDatedFilename } from "../../../utils/formattingFns";
import NewPopupTabs from "../../../components/Tabs/NewPopupTabs";
import TagsField from "../../../components/Form/TagsField";
import usePost from "../../../hooks/endpoints/usePost";
import ModalBase from "../../../components/Popups/ModalBase";


const tabs = [ 'Import file', 'Convert CAS list' ];

const defaultForm: UploadDataFileRequest = {
    name: '',
    file_metadata: {
        tags: [],
    },
    datafile_metadata: {
        description: '',
        measurement_type: '',
        target: '',
        organism: '',
    }
}


export default function UploadFileModal({
    setPopupMessage,
    refetchFiles,
}: {
    setPopupMessage : (popupMessage: string, isSuccessMessage: boolean) => void,
    refetchFiles: () => void,
}) {
    const [open, setOpen] = useState(false);
    const handleOpen = () => setOpen(true);
    const [ showSelect, setShowSelect ] = useState(false);

    const [ selectedIndex, setSelectedIndex ] = useState(0);
    const [ formBtns, setFormBtns ] = useState<Btn[]>([]);
    const [ form, setForm ] = useState<UploadDataFileRequest>(defaultForm);
    const [ casList, setCasList ] = useState('');
    const [ uploadedFile, setUploadedFile ] = useState<File | null>(null);
    const [ isDisabled, setIsDisabled ] = useState(false);
    const [ isLoading, setIsLoading ] = useState(false);

    const handleClose = () => {
        setOpen(false);
        resetValues();
        setShowSelect(false);
    };

    const resetValues = () => {
        setSelectedIndex(0);
        setForm(defaultForm);
        setCasList('');
        setUploadedFile(null);
        setIsDisabled(false);
        setIsLoading(false);
    }

    const { handleUploadFile } = usePost({
        setPopupMessage,
        setIsDisabled,
        setIsLoading,
        refetchFn: refetchFiles,
        handleClose,
    });

    useEffect(() => {
        if (!form.name) {
            setIsDisabled(true);
        } else {
            if (tabs[selectedIndex] === 'Import file' && !uploadedFile) {
                setIsDisabled(true);
            } else if (tabs[selectedIndex] === 'Convert CAS list' && !casList) {
                setIsDisabled(true);
            } else {
                setIsDisabled(false);
            }
        }
    }, [casList, uploadedFile, form.name, selectedIndex]); 

    useEffect(() => {
        setFormBtns([
            {
                type: "tertiary",
                styling: "outline",
                id: 'cancel-file-upload',
                label: 'Cancel',
                action: (e: MouseEvent<HTMLButtonElement>) => handleClose(),
            },
            {
                type: isDisabled ? 'disabled' : 'primary',
                styling: "solid",
                id: 'import-file-btn',
                label: "Import",
                action: (e: MouseEvent<HTMLButtonElement>) => handleUploadFile(e, tabs[selectedIndex], form, uploadedFile, casList),
                isLoading,
            }

        ])
        
        //eslint-disable-next-line
    }, [isDisabled, isLoading, casList, form, uploadedFile, selectedIndex]);


    // Files accepted from drag and drop 
    const onDrop = useCallback((acceptedFiles: File[]) => {
        setUploadedFile(acceptedFiles[0]);
        const { name } = acceptedFiles[0];
        setForm({...form, name});
        //eslint-disable-next-line
    }, []);

    const autoGenerateName = (e: MouseEvent<HTMLButtonElement>) => {
        e.preventDefault();
        if (tabs[selectedIndex] === 'Import file' && uploadedFile) {
            setForm({...form, name: uploadedFile.name});
        } else if (tabs[selectedIndex] === 'Convert CAS list' && casList) {
            setForm({...form, name: getDatedFilename('CAS_List')})
        }
    }

    const handleCASInput = (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        setCasList(e.target.value);
    }

    const handleInput = (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const name = e.target.name;
        const value = e.target.value;

        setForm({...form, [name]: value})
    };
    

    const handleMetadataInput = (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const name = e.target.name;
        const value = e.target.value;

        setForm({
            ...form,
            datafile_metadata: {
                ...form.datafile_metadata,
                [name]: value,
            }
        })
    }

    const handleAddTag = (category: string, tag: string) => {
        let addedTag = '';

        if (category === 'general') {
            addedTag = tag;
        } else {
            addedTag = `${category}::${tag}`
        }

        setForm({
            ...form,
            file_metadata: {
                ...form.file_metadata,
                tags: form.file_metadata!.tags!.concat(addedTag)
            }
        })
    }

    const handleRemoveTag = (index: number) => {
        setForm({
            ...form,
            file_metadata: {
                ...form.file_metadata,
                tags: form.file_metadata!.tags!.filter((t, i) => i !== index)
            }
        })
    }

    const handleTabChange = (event: React.SyntheticEvent, index: number) => {
        setSelectedIndex(index);
    }

    return (
        <div>
            <Buttons 
                id='upload-file-button' 
                action={handleOpen} 
                type="primary" 
                label="Upload File" 
                styling="solid" 
                startingAdornment={<PlusIcon/>}
            />
            <ModalBase
                open={open}
                setShowSelect={setShowSelect}
                modalId="file-upload-popup"
            >
                <div className={`bg-white rounded-lg max-w-[600px] w-full popup-max-height mx-auto`}>
                    <div className='flex flex-col justify-center items-stretch popup-max-height'> 
                        <PopupHeader
                            label="Upload File"
                            hasOutline={false}
                            closeFn={handleClose}
                        />   
                        <div className="px-6">
                            <NewPopupTabs 
                                tabs={tabs.map((tab,i) => ({
                                    label: tab,
                                    index: i
                                }))}
                                selectedIndex={selectedIndex}
                                handleTabChange={handleTabChange}
                            />
                        </div>
                        <div className="w-full flex flex-col gap-8 p-6 justify-start items-stretch overflow-y-auto">
                            
                            <div className="w-full">
                                {tabs[selectedIndex] === 'Import file' ? (
                                    <>
                                    {uploadedFile ? (
                                        <div className="w-full flex flex-col justify-center items-start gap-2 text-default">
                                            <label className={`text-[12px] font-medium`}>Selected File</label>
                                            <SelectedItem 
                                                type="file"
                                                name={uploadedFile.name}
                                                metadata={formatBytes(uploadedFile.size)}
                                                fileExt={getFileExt(uploadedFile.type)}
                                                deleteFn={(e: MouseEvent<HTMLButtonElement>) => {setUploadedFile(null); setForm({...form, name: ''})}}
                                            />
                                        </div>
                                    ) : (
                                        <DragAndDropRawFile onDrop={onDrop}/>
                                    )}
                                    </>
                                ) : (
                                    <InputField 
                                        label="CAS list"
                                        type='textarea'
                                        id='cas-list'
                                        name='cas-list'
                                        value={casList}
                                        isRequired={true}
                                        handleChange={handleCASInput}
                                    />
                                )}
                            </div>
                            <div className="w-full flex justify-between items-end gap-4">
                                <InputField 
                                    label="File name"
                                    type='text'
                                    id='name'
                                    name='name'
                                    value={form.name as string}
                                    isRequired
                                    handleChange={handleInput}
                                />
                                <Buttons
                                    id='autogenerate-btn'
                                    type='secondary'
                                    styling="solid"
                                    label='Autogenerate'
                                    startingAdornment={<AutogenerateIcon/>}
                                    action={autoGenerateName}
                                />
                            </div>
                            <div className="w-full flex justify-between items-end gap-4">
                                <InputField 
                                    label="Organism"
                                    type='text'
                                    id='organism'
                                    name='organism'
                                    value={form.datafile_metadata!.organism as string}
                                    isRequired={false}
                                    handleChange={handleMetadataInput}
                                />
                                <InputField 
                                    label="Measurement Type"
                                    type='text'
                                    id='measurement_type'
                                    name='measurement_type'
                                    value={form.datafile_metadata!.measurement_type as string}
                                    isRequired={false}
                                    handleChange={handleMetadataInput}
                                />
                                <InputField 
                                    label="Target"
                                    type='text'
                                    id='target'
                                    name='target'
                                    value={form.datafile_metadata!.target as string}
                                    isRequired={false}
                                    handleChange={handleMetadataInput}
                                />
                            </div>
                            <div className="w-full">
                                <InputField 
                                    label="Description"
                                    type='textarea'
                                    id='description'
                                    name='description'
                                    value={form.datafile_metadata!.description as string}
                                    isRequired={false}
                                    handleChange={handleMetadataInput}
                                />
                            </div>
                            <TagsField 
                                label="File"
                                tags={form.file_metadata!.tags! as string[]}
                                showSelect={showSelect}
                                menuPortalTarget={document.getElementById('file-upload-popup')}
                                handleAddTag={handleAddTag}
                                handleRemoveTag={handleRemoveTag}
                            />
                        </div>
                        <PopupFooter hasOutline={true} buttons={formBtns}/>
                    </div>
                </div>
            </ModalBase>
        </div>
    )
}